import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, Database, FileText, KeyRound, Activity, Mail, Building2,
  ClipboardCheck, RefreshCw, FileDown, CheckCircle2, Clock, Circle,
} from "lucide-react";
import TopNav from "../components/TopNav";
import { TacticalFooter } from "./landing/Cta";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_ICON = {
  "Access Control": KeyRound,
  "Data Isolation": Database,
  "Encryption": Lock,
  "Audit & Monitoring": Activity,
  "Email Security": Mail,
  "Vendor Management": Building2,
  "Formal Audit": ClipboardCheck,
  "Business Continuity": RefreshCw,
};

const STATUS_META = {
  implemented: { color: "var(--green)", label: "Implemented", icon: CheckCircle2 },
  in_progress: { color: "var(--amber)", label: "In Progress", icon: Clock },
  planned: { color: "var(--red)", label: "Planned", icon: Circle },
};

export default function Trust() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${PUBLIC_API}/security/controls`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.message));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav landing />
      {/* Hero */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="overline">// TRUST_CENTER</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tight mt-2" data-testid="trust-title">
            Security <span className="text-[var(--cyan)] cyan-text-glow">posture</span>, in the open.
          </h1>
          <p className="mt-4 max-w-2xl text-[var(--text-2)] text-base">
            Every control listed below is implemented in the DefDossier codebase or runtime configuration.
            Controls still in progress or planned are disclosed honestly — no implied certifications, ever.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`${PUBLIC_API}/security/pack.pdf`} target="_blank" rel="noreferrer"
               className="btn-primary inline-flex items-center gap-2" data-testid="download-pack">
              <FileDown size={16} /> Download Security Pack (PDF)
            </a>
            <Link to="/register" className="btn-ghost inline-flex items-center gap-2">
              Start 30-Day Pilot →
            </Link>
          </div>
        </div>
      </section>

      {err && <div className="max-w-7xl mx-auto px-6 mt-8 font-mono text-xs text-[var(--red)]">{err}</div>}

      {data && (
        <>
          {/* Stats */}
          <section className="border-b border-[var(--border)]">
            <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-[var(--border)] border border-[var(--border)]" data-testid="trust-summary">
              {[
                ["TOTAL", data.summary.total, "var(--text-1)"],
                ["IMPLEMENTED", data.summary.implemented, "var(--green)"],
                ["IN PROGRESS", data.summary.in_progress, "var(--amber)"],
                ["PLANNED", data.summary.planned, "var(--red)"],
                ["COVERAGE", `${data.summary.coverage_pct}%`, "var(--cyan)"],
              ].map(([k, v, color]) => (
                <div key={k} className="bg-[var(--bg)] p-5">
                  <div className="overline">{k}</div>
                  <div className="font-mono text-3xl mt-1" style={{ color }}>{v}</div>
                </div>
              ))}
            </div>
            <div className="max-w-7xl mx-auto px-6 pb-6 font-mono text-xs text-[var(--text-2)]">
              // as of {data.summary.as_of}
            </div>
          </section>

          {/* Controls by domain */}
          <section className="border-b border-[var(--border)]">
            <div className="max-w-7xl mx-auto px-6 py-14">
              <div className="overline">// CONTROL_INVENTORY</div>
              <h2 className="font-display font-bold text-3xl uppercase mt-2 mb-10">Controls by domain</h2>
              <div className="space-y-10">
                {data.domains.map((d) => {
                  const Icon = DOMAIN_ICON[d.domain] || ShieldCheck;
                  return (
                    <div key={d.domain} data-testid={`domain-${d.domain.replace(/\s+/g, "-").toLowerCase()}`}>
                      <div className="flex items-center gap-3 mb-4 border-b border-[var(--border)] pb-3">
                        <Icon size={20} color="var(--cyan)" strokeWidth={1.5} />
                        <h3 className="font-display font-bold uppercase text-xl tracking-widest">{d.domain}</h3>
                        <span className="font-mono text-xs text-[var(--text-2)]">// {d.controls.length}</span>
                      </div>
                      <div className="space-y-3">
                        {d.controls.map((c) => {
                          const meta = STATUS_META[c.status] || STATUS_META.planned;
                          const StIcon = meta.icon;
                          return (
                            <div key={c.id} className="flat-card p-5" data-testid={`control-${c.id}`}>
                              <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-xs text-[var(--cyan)]">{c.id}</span>
                                    <span className="font-display font-bold uppercase">{c.name}</span>
                                  </div>
                                  <p className="text-sm text-[var(--text-2)] mt-2 leading-relaxed">{c.evidence}</p>
                                  <div className="font-mono text-[10px] text-[var(--text-2)] mt-2">
                                    // {c.code_ref}
                                  </div>
                                </div>
                                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase px-2 py-1 border whitespace-nowrap"
                                      style={{ color: meta.color, borderColor: meta.color }}>
                                  <StIcon size={11} strokeWidth={1.5} /> {meta.label}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Subprocessors */}
          <section className="border-b border-[var(--border)] bg-[var(--surface)]">
            <div className="max-w-7xl mx-auto px-6 py-14">
              <div className="overline">// SUBPROCESSORS</div>
              <h2 className="font-display font-bold text-3xl uppercase mt-2 mb-3">Data subprocessors</h2>
              <p className="text-sm text-[var(--text-2)] mb-8 max-w-2xl">
                The only third parties that touch customer data. Any change to this list is reflected here within 30 days.
              </p>
              <div className="flat-card overflow-x-auto" data-testid="subprocessor-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
                      <th className="text-left p-3 font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">Vendor</th>
                      <th className="text-left p-3 font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">Purpose</th>
                      <th className="text-left p-3 font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">Data category</th>
                      <th className="text-left p-3 font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">Region</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.subprocessors.map((s) => (
                      <tr key={s.name} className="border-b border-[var(--border)] last:border-0">
                        <td className="p-3 font-display font-bold uppercase">
                          <a href={s.url} target="_blank" rel="noreferrer" className="hover:text-[var(--cyan)]">{s.name}</a>
                        </td>
                        <td className="p-3 text-[var(--text-2)]">{s.purpose}</td>
                        <td className="p-3 text-[var(--text-2)]">{s.data}</td>
                        <td className="p-3 font-mono text-xs text-[var(--cyan)]">{s.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Honest disclaimer */}
          <section className="border-b border-[var(--border)]">
            <div className="max-w-7xl mx-auto px-6 py-12">
              <div className="overline">// HONESTY_POLICY</div>
              <h2 className="font-display font-bold text-2xl uppercase mt-2 mb-4">What this page is not</h2>
              <p className="text-[var(--text-2)] text-sm leading-relaxed max-w-3xl">
                This is not a formal audit report. DefDossier does not currently hold SOC 2 or
                ISO 27001 attestation — both are explicitly listed under <em>Formal Audit</em> as planned.
                If you require third-party attestation today, ask us before contracting. We will route
                your requirement to our auditor partner timeline, or — if it's blocking — propose an
                alternative path.
              </p>
              <p className="text-[var(--text-2)] text-sm mt-4">
                Questions? Email <a href="mailto:joel@defdossier.com" className="text-[var(--cyan)] hover:underline">joel@defdossier.com</a>.
              </p>
            </div>
          </section>
        </>
      )}
      <TacticalFooter />
    </div>
  );
}
